import { component, observable, computed, subscribe } from 'knockout-decorators'
import * as ko from 'knockout'
import {
  TouristTaxState,
  type TouristTaxGuestData,
} from '../../state/tourist-tax'

import { EventState } from '~/store/event'
import source from './template.html?raw'

import { Language, isMinorByEventStartDate } from '~/utils'
import { countries } from '~/common/data/countries'
import { CountryCode } from '@tixa/schema'
import UserIcon from '../../../../images/icons/heroicons/user-circle.svg?raw'
import EyeIcon from '../../../../images/icons/heroicons/eye.svg?raw'
import EyeSlashIcon from '../../../../images/icons/heroicons/eye-slash.svg?raw'

import './style.less'

@component('tourist-tax-content', source)
export class TouristTaxContent {
  private touristTaxState = TouristTaxState.get()
  private Locale = Language.getInstance()
  private EventState = EventState.get()
  public userIcon = UserIcon
  public eyeIcon = EyeIcon
  public eyeSlashIcon = EyeSlashIcon

  @observable public availableCountries: CountryCode[] = []
  @observable public selectedCountry = TouristTaxState.DEFAULT_COUNTRY
  @observable public formData!: TouristTaxGuestData
  public ticketTypeId!: number
  public contentId!: number
  public globalIndex!: number
  public localIndex!: number
  @observable public isCollapsed: boolean
  @observable public isTaxable: boolean
  public isTaxFree: ko.Computed<boolean>
  @observable private nameUpdateTrigger = 0

  constructor(params: {
    formData: TouristTaxGuestData
    ticketTypeId: number
    contentId: number
    globalIndex: number
    localIndex: number
  }) {
    this.formData = params.formData
    this.ticketTypeId = params.ticketTypeId
    this.contentId = params.contentId
    this.globalIndex = params.globalIndex
    this.localIndex = params.localIndex
    this.isCollapsed = false
    this.isTaxable = false

    this.isTaxFree = ko.computed({
      read: () => {
        return this.formData.isTaxFree ?? false
      },
      write: (value: boolean) => {
        this.formData.isTaxFree = value
        const contentItem = this.touristTaxState.content.find(
          (c) => c.ticketTypeId === this.ticketTypeId
        )
        if (contentItem) {
          contentItem.guests.valueHasMutated()
        }
      },
    })

    this.initializeCountries()

    if (this.formData.dateOfBirth) {
      this.isTaxableByDateOfBirth()
    }

    subscribe<boolean>(
      () => this.touristTaxState.hasErrors,
      (hasErrors) => {
        if (hasErrors && this.globalIndex !== 0) {
          this.isCollapsed = false
        }
      }
    )

    subscribe<CountryCode>(
      () => this.selectedCountry,
      (newCountry) => {
        if (newCountry) {
          this.formData.country = newCountry.name
        }
      }
    )
  }

  public i18n(key: string): string {
    return this.Locale.translations[key]
  }

  @computed
  public get maxDateOfBirth(): string {
    // Today's date in YYYY-MM-DD format
    return new Date().toISOString().split('T')[0]
  }

  @computed
  public get minDateOfBirth(): string {
    // 150 years ago in YYYY-MM-DD format
    const date = new Date()
    date.setFullYear(date.getFullYear() - 150)
    return date.toISOString().split('T')[0]
  }

  public toggleCollapse() {
    this.isCollapsed = !this.isCollapsed
  }

  public removeGuest() {
    this.touristTaxState.removeSpecificGuestById(
      this.contentId,
      this.localIndex
    )
  }

  public onCountryChange() {
    if (this.selectedCountry) {
      this.formData.country = this.selectedCountry.name
      this.formData.selectedCountryId = this.selectedCountry.id
    }
  }

  public onFieldChange(fieldName: string) {
    const fieldId = `${fieldName}-${this.globalIndex}`
    this.touristTaxState.clearFieldError(fieldId)

    if (fieldName === 'name') {
      this.nameUpdateTrigger++
    }

    // Sync isTaxFree state when it changes
    if (fieldName === 'isTaxFree') {
      setTimeout(() => {
        const checkboxElement = document.querySelector(
          `#${fieldId}`
        ) as HTMLInputElement
        const isChecked = checkboxElement?.checked ?? false

        this.formData.isTaxFree = isChecked

        const contentItem = this.touristTaxState.content.find(
          (c) => c.ticketTypeId === this.ticketTypeId
        )
        if (contentItem) {
          contentItem.guests.valueHasMutated()
        }
      }, 0)
    }
  }

  @computed
  public get displayName(): string {
    this.nameUpdateTrigger
    return this.formData.name && this.formData.name.trim() !== ''
      ? this.formData.name
      : ''
  }

  @computed
  public get canRemoveGuest(): boolean {
    const guest = this.touristTaxState.content.find(
      (g) => g.id === this.contentId
    )

    return guest ? guest.guests().length > guest.quantityMin : false
  }

  private initializeCountries() {
    this.availableCountries = Object.values(
      countries[this.Locale.language as keyof typeof countries]
    )

    const countryId =
      this.formData.selectedCountryId || TouristTaxState.DEFAULT_COUNTRY_ID
    this.selectedCountry =
      this.availableCountries.find(({ id }) => id === countryId) ||
      TouristTaxState.DEFAULT_COUNTRY

    this.formData.country = this.selectedCountry.name
    this.formData.selectedCountryId = this.selectedCountry.id
  }

  public isTaxableByDateOfBirth() {
    if (!this.formData.dateOfBirth) return false

    this.isTaxable = isMinorByEventStartDate(
      this.EventState.event?.startDate || new Date(),
      this.formData.dateOfBirth
    )

    if (!this.isTaxable) {
      this.isTaxFree(false)
    }
  }
}
